// @ts-nocheck
import { memo, useEffect, useRef, useState } from "react";
import {
  Button,
  FormControl,
  MenuItem,
  Select,
  Box,
  TextField,
  Radio,
  ListItemText,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { AutoSizer, List } from "react-virtualized";
import "./multiselect.scss";

interface SingleSelectWithRadioProps {
  options: { value: any; label: string; }[]; // options should have value and label;
  label?: string;
  value: string;
  onApply: (value: any) => void;
  placeholder?: string;
  disabled?: boolean;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MAX_MENU_HEIGHT = ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP;
const MENU_WIDTH = 250;

const SingleSelectWithRadio: React.FC<SingleSelectWithRadioProps> = ({
  options,
  label,
  value,
  onApply,
  disabled,
  placeholder,
}) => {
  const [tempSelectedValue, setTempSelectedValue] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setTempSelectedValue(value);
  }, [value]);

  const handleSelectChange = (itemValue: string) => {
    setTempSelectedValue(itemValue); // Update to only one selected option
  };

  const handleApply = () => {
    if (!!tempSelectedValue) {
      onApply(tempSelectedValue); // Pass an array with only the selected value
    }
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchQuery(""); // Reset search query on close
  };

  const handleOpen = () => {
    setOpen(true);
    setSearchQuery("");
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent select from closing when clicking inside the search box
  };

  const filteredOptions = options.filter(option =>
    option?.label?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const rowRenderer = ({ index, key, style }: { index: number; key: string; style: React.CSSProperties }) => {
    const option = filteredOptions[index];
    return (
      <MenuItem
        key={key}
        selected={true}
        value={option?.value}
        style={style}
        disableRipple
        onClick={() => handleSelectChange(option?.value)}
      >
        <ListItemText primary={option?.label} />
        <Radio checked={tempSelectedValue === option?.value} /> {/* Single selection: check if option matches selected */}
      </MenuItem>
    );
  };

  return (
    <FormControl fullWidth className={`${disabled ? "disabled" : ""} select-wrapper`}>
      {label && <label>{label} <span>*</span></label>}
      <Select
        labelId="multi-select-label"
        value={value} // Single selection, just pass the string value
        IconComponent={ExpandMore}
        onClose={handleClose}
        open={open}
        onOpen={handleOpen}
        disabled={disabled}
        placeholder={placeholder || "Search..."}
        renderValue={(selected) =>
          options?.find((option) => option?.value === selected)?.label || ""
        }
        className='single-select'
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: MAX_MENU_HEIGHT,
              width: MENU_WIDTH,
            },
            className: "multi-select-wrapper-without-clear",
          },
        }}
      >
        <MenuItem>
          <TextField
            inputRef={inputRef} // Reference to the input
            placeholder={placeholder || "Search..."}
            fullWidth
            onChange={(e) => setSearchQuery(e.target.value)}
            value={searchQuery}
            onClick={handleInputClick} // Prevent closing on click
          />
        </MenuItem>
        <AutoSizer disableHeight>
          {({ width }: { width: number }) => (
            <List
              width={width}
              height={Math.min(ITEM_HEIGHT * 4.5, 300)}
              rowCount={filteredOptions.length}
              rowHeight={ITEM_HEIGHT}
              rowRenderer={rowRenderer}
              overscanRowCount={5}
            />
          )}
        </AutoSizer>

        <Box className="flex justify-content-center align-items-center list-footer">
          <Button onClick={handleApply} className="primary-medium-green-btn" disableRipple>
            Done
          </Button>
        </Box>
      </Select>
    </FormControl>
  );
};

export default memo(SingleSelectWithRadio);
